import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';
import { useEntitlements } from '../hooks/useEntitlements';

/**
 * Locations (branches) — Phase 5. A wall's scheduled playlist resolves in its
 * location's timezone.
 *   GET/POST   /locations
 *   GET/PUT/DELETE /locations/{id}
 *   POST /locations/{id}/walls   set members { wall_ids }
 * Resource-wrapped; unwrap with res?.data ?? res.
 */
const LIST_KEY = 'locations';
const itemKey = (id) => ['location', id];

// `enabled` only turns off for a package that explicitly lacks `locations` (the backend would answer 402).
export const useGetLocations = () => {
  const { has } = useEntitlements();
  return useQuery(LIST_KEY, async () => {
    const res = (await axiosInstance.get('/locations')).data;
    return (res?.data ?? res)?.locations ?? [];
  }, { staleTime: 15_000, enabled: has('locations') });
};

export const useGetLocation = (id, enabled = true) => {
  const { has } = useEntitlements();
  return useQuery(itemKey(id), async () => {
    const res = (await axiosInstance.get(`/locations/${id}`)).data;
    return (res?.data ?? res)?.location ?? null;
  }, { enabled: !!id && enabled && has('locations'), staleTime: 5_000 });
};

const mut = (fn, okMsg) => () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(fn, {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); if (okMsg) showToast('success', 'Locations', okMsg); },
    onError: (err) => showToast('error', 'Locations', err.response?.data?.message),
  });
};

export const useCreateLocation = mut((payload) => axiosInstance.post('/locations', payload), 'Location created.');
export const useUpdateLocation = mut(({ id, ...p }) => axiosInstance.put(`/locations/${id}`, p), 'Location updated.');
export const useDeleteLocation = mut((id) => axiosInstance.delete(`/locations/${id}`), 'Location deleted.');
export const useSetLocationWalls = mut(({ id, wall_ids }) => axiosInstance.post(`/locations/${id}/walls`, { wall_ids }), 'Walls updated.');

// Best-effort country -> IANA timezone, mirrors backend General::countryToTimezone
// (timezone system completion). Lets the Location form auto-suggest a tz from the
// typed country. Case-insensitive; returns null when unknown.
const COUNTRY_TZ = {
  'saudi arabia': 'Asia/Riyadh', ksa: 'Asia/Riyadh', kuwait: 'Asia/Kuwait',
  qatar: 'Asia/Qatar', bahrain: 'Asia/Bahrain', 'united arab emirates': 'Asia/Dubai',
  uae: 'Asia/Dubai', oman: 'Asia/Muscat', jordan: 'Asia/Amman', lebanon: 'Asia/Beirut',
  syria: 'Asia/Damascus', iraq: 'Asia/Baghdad', palestine: 'Asia/Hebron', yemen: 'Asia/Aden',
  egypt: 'Africa/Cairo', turkey: 'Europe/Istanbul', morocco: 'Africa/Casablanca',
  tunisia: 'Africa/Tunis', algeria: 'Africa/Algiers', libya: 'Africa/Tripoli',
  sudan: 'Africa/Khartoum', pakistan: 'Asia/Karachi', india: 'Asia/Kolkata',
  usa: 'America/New_York', 'united states': 'America/New_York', uk: 'Europe/London',
  'united kingdom': 'Europe/London', france: 'Europe/Paris', germany: 'Europe/Berlin',
};
export const countryToTimezone = (country) => {
  if (!country) return null;
  return COUNTRY_TZ[String(country).trim().toLowerCase()] || null;
};

// Curated country list (display label + timezone) for the inline "Create Branch"
// shortcut — self-contained, no dependency on the (broken) GeoNames API.
export const countryTzList = [
  { label: 'Saudi Arabia', timezone: 'Asia/Riyadh' },
  { label: 'Kuwait', timezone: 'Asia/Kuwait' },
  { label: 'Qatar', timezone: 'Asia/Qatar' },
  { label: 'Bahrain', timezone: 'Asia/Bahrain' },
  { label: 'United Arab Emirates', timezone: 'Asia/Dubai' },
  { label: 'Oman', timezone: 'Asia/Muscat' },
  { label: 'Jordan', timezone: 'Asia/Amman' },
  { label: 'Lebanon', timezone: 'Asia/Beirut' },
  { label: 'Syria', timezone: 'Asia/Damascus' },
  { label: 'Iraq', timezone: 'Asia/Baghdad' },
  { label: 'Palestine', timezone: 'Asia/Hebron' },
  { label: 'Yemen', timezone: 'Asia/Aden' },
  { label: 'Egypt', timezone: 'Africa/Cairo' },
  { label: 'Turkey', timezone: 'Europe/Istanbul' },
  { label: 'Morocco', timezone: 'Africa/Casablanca' },
  { label: 'Tunisia', timezone: 'Africa/Tunis' },
  { label: 'Algeria', timezone: 'Africa/Algiers' },
  { label: 'Libya', timezone: 'Africa/Tripoli' },
  { label: 'Sudan', timezone: 'Africa/Khartoum' },
  { label: 'Pakistan', timezone: 'Asia/Karachi' },
  { label: 'India', timezone: 'Asia/Kolkata' },
  { label: 'United States', timezone: 'America/New_York' },
  { label: 'United Kingdom', timezone: 'Europe/London' },
  { label: 'France', timezone: 'Europe/Paris' },
  { label: 'Germany', timezone: 'Europe/Berlin' },
];

// "Asia/Kuwait (UTC+3)" — the timezone plus its current UTC offset.
export const tzLabel = (tz) => {
  if (!tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date());
    const off = (parts.find((p) => p.type === 'timeZoneName')?.value || '').replace('GMT', 'UTC');
    return off ? `${tz} (${off})` : tz;
  } catch (e) { return tz; }
};

/** Best-effort timezone list for the picker (falls back to a regional shortlist). */
export const timezoneOptions = (() => {
  try {
    if (typeof Intl.supportedValuesOf === 'function') return Intl.supportedValuesOf('timeZone');
  } catch (e) { /* ignore */ }
  return ['Asia/Riyadh', 'Asia/Dubai', 'Asia/Kuwait', 'Asia/Bahrain', 'Asia/Qatar',
    'Africa/Cairo', 'Europe/Istanbul', 'Europe/London', 'America/New_York', 'UTC'];
})();
